/**
 * SIZE_PRESETS — per-kind S/M/L pixel dimensions. Spawning picks M.
 * Freeform drag-resize in EditableItem clamps to [S, L] for that kind.
 *
 * Buttons are shape-aware — the values in SIZE_PRESETS.button are a
 * legacy fallback for callers that don't know the button's shape.
 * Shape-aware paths (spawnButton, InspectorPanel size pills, resize
 * clamps) go through `buttonSizeFor(shape, size)` and `buttonClampsFor
 * (shape)` in shapes.ts, which reads per-shape presets from SHAPE_LIBRARY.
 *
 * StreamDeck stays square (still the square-lock kind).
 * LFO/Macro/Sequencer can be scaled non-uniformly within the clamps.
 * ButtonGrid pixel dimensions are derived from rows × cols × cell-size,
 * so its size preset only changes per-cell pixels (S/M/L cells), not
 * the rows × cols count.
 */
import type { SizePreset, ButtonShape } from './projectStore.ts';
import { shapeSizesFor, shapeAspectRule } from './shapes.ts';

export interface SizeDims { w: number; h: number }

export interface KindSizes {
  S: SizeDims;
  M: SizeDims;
  L: SizeDims;
}

export const SIZE_PRESETS: Record<
  'button' | 'lfo' | 'buttongrid' | 'sequencer' | 'macro' | 'streamdeck'
  | 'fader' | 'xypad' | 'timelineLauncher' | 'piano' | 'image' | 'statusLed'
  | 'transpose' | 'capo' | 'recQuant' | 'abletonRow' | 'sceneCopy' | 'wavesAutotune' | 'mixer' | 'radial' | 'label'
  | 'presetChange' | 'snapshotChange' | 'display' | 'rigStateButton'
  | 'lightColor' | 'lightLevel' | 'lightFx' | 'blackout'
  | 'opvisualTrigger' | 'opvisualGrid' | 'synesthesiaGrid' | 'audioPads',
  KindSizes
> = {
  // Legacy fallback (rect shape). Shape-aware callers go through
  // buttonSizeFor(shape, size).
  button:    { S: { w: 140, h: 100 }, M: { w: 200, h: 140 }, L: { w: 280, h: 200 } },
  lfo:       { S: { w: 220, h: 140 }, M: { w: 280, h: 180 }, L: { w: 400, h: 280 } },
  buttongrid:{ S: { w:  60, h:  60 }, M: { w:  80, h:  80 }, L: { w: 120, h: 120 } }, // PER-CELL
  sequencer: { S: { w: 560, h: 220 }, M: { w: 720, h: 280 }, L: { w: 960, h: 360 } },
  macro:     { S: { w: 180, h: 120 }, M: { w: 220, h: 140 }, L: { w: 320, h: 220 } },
  streamdeck:{ S: { w: 120, h: 120 }, M: { w: 160, h: 160 }, L: { w: 220, h: 220 } },
  // Faders are tall, narrow bars by default. Vertical orientation; rotate
  // to horizontal at the item level. S/L still useful as resize clamps.
  fader:     { S: { w:  56, h: 180 }, M: { w:  72, h: 240 }, L: { w: 100, h: 360 } },
  // XY Pads are square by convention (Kaoss / TouchOSC vocabulary). Freeform
  // resize allowed but the aspect stays square in the item's own render.
  xypad:     { S: { w: 180, h: 180 }, M: { w: 240, h: 240 }, L: { w: 360, h: 360 } },
  // Timeline Launchers can hold many section buttons — width scales with
  // section count. Height stays modest (single row of section pills).
  timelineLauncher: { S: { w: 220, h: 100 }, M: { w: 340, h: 130 }, L: { w: 520, h: 180 } },
  // Piano is wide + short. Presets sized so a 2-octave M-preset piano
  // has ~48px-wide white keys — tappable on iPad. S = 1 octave typical,
  // L = 3 octaves. Freeform resize allowed within the clamp band; the
  // renderer distributes octaves across the actual pixel width.
  piano:     { S: { w: 240, h: 100 }, M: { w: 420, h: 140 }, L: { w: 720, h: 180 } },
  // Images default to ~16:10. Real dimensions get clamped to the asset's
  // natural aspect on import, but the preset still bounds the resize.
  image:     { S: { w: 160, h: 100 }, M: { w: 320, h: 200 }, L: { w: 640, h: 400 } },
  // Status LEDs are small — they're indicators, not affordances. Default
  // preset lands around a fingernail-sized dot on iPad. Shape swaps
  // (circle vs rect vs pill) reuse the same clamps but read differently.
  statusLed: { S: { w:  40, h:  40 }, M: { w:  60, h:  60 }, L: { w:  90, h:  90 } },
  // Preset-change tiles are read at arm's length on stage — the preset NAME
  // is the whole point, so they get a button-ish footprint with enough width
  // for ~16 characters rather than a square. Wider than tall deliberately.
  presetChange: { S: { w: 160, h: 90 }, M: { w: 220, h: 120 }, L: { w: 320, h: 160 } },
  // Snapshot tiles are NARROWER than preset tiles: their face is a short
  // label ("LEAD", "SNAP 3"), not a 16-character preset name, and the
  // canonical use is a row of eight across the width of the rig. Sized so
  // 8 at M fit an iPad landscape canvas with gaps.
  snapshotChange: { S: { w: 100, h: 84 }, M: { w: 130, h: 110 }, L: { w: 190, h: 150 } },
  // Transpose is a single row of semitone buttons — PER-CELL dims, like
  // buttongrid. Total width = cellW × (end - start + 1). Slightly wider
  // than a grid cell so "-12"/"+12" labels breathe.
  transpose: { S: { w:  48, h:  48 }, M: { w:  60, h:  60 }, L: { w:  84, h:  84 } }, // PER-CELL
  // Capo is the same semitone-row shape as transpose — PER-CELL.
  capo:      { S: { w:  48, h:  48 }, M: { w:  60, h:  60 }, L: { w:  84, h:  84 } }, // PER-CELL
  // Record quantization is the same one-row PER-CELL shape, but its faces
  // are LABELS ("1/16T") not two-character numbers, so the cells have to be
  // wider than a semitone pad or the text truncates. The two combo values
  // ("1/8 + 1/8T") wrap to two lines in the component rather than forcing
  // every cell wide enough for the worst case.
  recQuant:  { S: { w:  64, h:  48 }, M: { w:  80, h:  60 }, L: { w: 108, h:  84 } }, // PER-CELL
  // An Ableton Control full row (DEC-291) sits beside the Record quantize row
  // and its faces are the same size of thing ("1 Bar", "6/8", "Play"), so its
  // cells are the same size: a page of them lines up.
  abletonRow: { S: { w:  64, h:  48 }, M: { w:  80, h:  60 }, L: { w: 108, h:  84 } }, // PER-CELL
  // Scene Copy is PER-CELL but stacks the other way from transpose: total =
  // cellW × 2 (the COPY and PASTE columns) wide, cellH × count tall. Cells
  // are WIDER than transpose's and a little shorter, because each face
  // carries two lines ("PASTE" over "5") rather than one short label, and
  // "PASTE" is the string that has to fit.
  sceneCopy: { S: { w:  72, h:  44 }, M: { w:  92, h:  56 }, L: { w: 124, h:  76 } }, // PER-CELL
  // Waves Autotune is PER-CELL like transpose, but total = cellW × keyCount
  // wide and cellH × 3 tall (two key rows + one MIN/MAJ/Chrom scale row).
  // Cells slightly shorter than transpose so three stacked rows stay compact.
  wavesAutotune: { S: { w: 48, h: 40 }, M: { w: 60, h: 52 }, L: { w: 84, h: 72 } }, // PER-CELL
  // Mixer is PER-STRIP: the values are one channel strip (width x full strip
  // height). Total item = trackCount x cellW wide, cellH tall. Strips are
  // tall + narrow for a real fader throw; 8 M-strips = 512x300, inside the
  // iPad landscape frame. See MIXER-PAGE-DESIGN.md section 5.
  mixer:     { S: { w: 52, h: 240 }, M: { w: 64, h: 300 }, L: { w: 84, h: 380 } }, // PER-STRIP
  // Radial knobs are SQUARE (DEC-116) and compact — that squareness is the
  // whole reason to reach for one instead of a fader. M lands on the
  // TouchOSC corpus's own 150x150; S/L bracket it.
  radial:    { S: { w: 110, h: 110 }, M: { w: 150, h: 150 }, L: { w: 210, h: 210 } },
  // Labels are wide + short by default — they are headings, not blocks.
  // Freeform resize is wide open; these only bound the preset + clamps.
  label:     { S: { w: 160, h:  44 }, M: { w: 240, h:  60 }, L: { w: 380, h:  88 } },
  // Display is a 3-line glance readout, closer to presetChange's "wide
  // enough for a name, read at arm's length" footprint than label's
  // single-line default - needs three text rows to breathe.
  display:   { S: { w: 160, h:  90 }, M: { w: 220, h: 130 }, L: { w: 320, h: 190 } },
  // Rig State buttons recall one named look - same "short label, row of
  // several" shape as snapshotChange, so it inherits its dims exactly.
  rigStateButton: { S: { w: 100, h: 84 }, M: { w: 130, h: 110 }, L: { w: 190, h: 150 } },
  // Lighting (2026-09-17). Colour / FX / Blackout track rigStateButton: they
  // are button-shaped and sit in the same rows. Level is a FADER, so it takes
  // the fader footprint verbatim rather than a new guess - a lighting fader
  // and a MIDI fader are the same gesture and must not want different room.
  lightColor:     { S: { w: 100, h: 84 }, M: { w: 130, h: 110 }, L: { w: 190, h: 150 } },
  lightFx:        { S: { w: 100, h: 84 }, M: { w: 130, h: 110 }, L: { w: 190, h: 150 } },
  blackout:       { S: { w: 100, h: 84 }, M: { w: 130, h: 110 }, L: { w: 190, h: 150 } },
  lightLevel:     { S: { w:  56, h: 180 }, M: { w:  72, h: 240 }, L: { w: 100, h: 360 } },
  // OPVisual (2026-09-24). The Trigger is button-shaped and sits in rows of
  // cells, so it takes the rigStateButton footprint. The Grid is a whole
  // surface: S still fits all 10 columns x 5 layers with the layer-control
  // gutter at a tappable cell size (720 wide = 52px cells, measured by
  // `_probe_opvisual_ten_columns`; 620 was right for 8 columns and gave 42px
  // at 10, under the 44px touch floor), M fills most of an iPad landscape
  // canvas (the spawner shrinks it to the measured canvas when that is
  // smaller), L is for big screens.
  opvisualTrigger: { S: { w: 100, h: 84 }, M: { w: 130, h: 110 }, L: { w: 190, h: 150 } },
  // Heights fit 16:9 cells at OPVisual's 10 columns plus the strips (DEC-272).
  opvisualGrid:    { S: { w: 720, h: 380 }, M: { w: 1060, h: 430 }, L: { w: 1600, h: 590 } },
  // Synesthesia Grid (2026-11-05): scene pictures over the moves and the look. M fills most of the iPad's canvas; L is
  // for a big screen and is capped to the canvas like the OPVisual Grid (store.ts `setItemSize`).
  synesthesiaGrid: { S: { w: 560, h: 380 }, M: { w: 900, h: 560 }, L: { w: 1400, h: 820 } },
  // ONE pad (a per-cell kind, `perCellDims`): the item is rows x cols of these. S keeps the 44px touch floor with room
  // for a name and the progress line.
  audioPads:       { S: { w: 84, h: 72 }, M: { w: 110, h: 94 }, L: { w: 150, h: 128 } },
};

/** Pixel dimensions for a kind at a given size preset. */
export function sizeFor(
  kind: keyof typeof SIZE_PRESETS,
  size: SizePreset,
): SizeDims {
  return SIZE_PRESETS[kind][size];
}

/** Shape-aware S/M/L pixel dims for a button. Callers holding a ButtonItem
 *  should prefer this over `sizeFor('button', size)`. */
export function buttonSizeFor(shape: ButtonShape, size: SizePreset): SizeDims {
  return shapeSizesFor(shape)[size];
}

/** Whether this kind's resize handle should preserve square aspect.
 *  Buttons are shape-aware — use `buttonClampsFor` for buttons instead
 *  (this returns false for `button` so legacy callers don't square-lock). */
export function isSquareKind(kind: keyof typeof SIZE_PRESETS): boolean {
  // radial joins streamdeck 2026-07-27 (DEC-116): a knob that resizes to a
  // rectangle stops reading as a knob, and squareness is the affordance.
  return kind === 'streamdeck' || kind === 'radial';
}

/** Min/max pixel dimensions for freeform drag-resize, per kind. */
export function clampsFor(kind: keyof typeof SIZE_PRESETS): {
  minW: number; minH: number; maxW: number; maxH: number;
} {
  const p = SIZE_PRESETS[kind];
  return {
    minW: p.S.w, minH: p.S.h,
    maxW: p.L.w, maxH: p.L.h,
  };
}

/** Absolute floor for button width and height. Locked 2026-07-03 —
 *  below this a button becomes untappable on iPad (fingertip contact
 *  patch is ~44px per Apple HIG, but the shape floor is set slightly
 *  lower so authors can still make LEDs / status indicators feel
 *  distinct from full buttons). Applied uniformly across every shape;
 *  a shape's own S preset may be larger, but never smaller. */
const BUTTON_MIN_W = 35;
const BUTTON_MIN_H = 35;

/** Shape-aware clamps + aspect rule for a button. EditableItem uses this
 *  for buttons; other kinds go through `clampsFor`. */
export function buttonClampsFor(shape: ButtonShape): {
  minW: number; minH: number; maxW: number; maxH: number;
  aspect: ReturnType<typeof shapeAspectRule>;
} {
  const p = shapeSizesFor(shape);
  return {
    minW: BUTTON_MIN_W, minH: BUTTON_MIN_H,
    maxW: p.L.w, maxH: p.L.h,
    aspect: shapeAspectRule(shape),
  };
}
